<script setup lang="ts">
import { computed, ref } from "vue";
import { useI18n } from "vue-i18n";
import { useRoute } from "vue-router";
import store from "@/store";

import { toast } from "@/common/utils/toast";

import { collectionPatch } from "@/common/api/rest/collection";
import { avatarAdd, avatarPut, avatarDel } from "@/common/api/rest/avatar";

import type { CollectionType } from "@/common/types/collection";
import type { TranslationFieldsType } from "@/common/types/translation";

import { FormCollection, ListTranslate } from "@/components";

import { baseAvatar, baseConfirm } from "@/common/components";

const { t, locale } = useI18n();
const route = useRoute();

const emit = defineEmits(["update:data"]);

const props = defineProps<{
  collection: CollectionType | null;
  collectionId: string;
}>();

const showAvatarDel = ref(false);

const translationFields = ref<TranslationFieldsType[]>([
  {
    type: "input",
    field: "name",
    label: "common.labels.name",
    value: "",
  },
  {
    type: "textarea",
    field: "description",
    label: "common.labels.description",
    value: "",
  },
]);

function patchData(payload: Partial<CollectionType>) {
  collectionPatch(props.collectionId, payload)
    .then((response) => {
      const { success, message } = response;

      if (success) {
        // emit("update:data");
      } else {
        toast({ message: message, type: "error" });
      }
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    });
}

const type = computed(() => route.params.type as string);

// AVATAR
function addAvatar(avatar: string) {
  if (!store.getters.user) return;

  store.commit("SET_LOADING", true);

  avatarAdd(props.collection.collection_id, avatar)
    .then((response) => {
      const { success, message } = response;

      toast({ message: message, type: success ? "success" : "error" });

      emit("update:data");
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    })
    .finally(() => {
      store.commit("SET_LOADING", false);
    });
}

function updateAvatar(avatar: string) {
  if (!props.collection.avatar) return;

  store.commit("SET_LOADING", true);

  avatarPut(props.collection.collection_id, avatar)
    .then((response) => {
      const { success, message } = response;

      toast({ message: message, type: success ? "success" : "error" });

      emit("update:data");
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    })
    .finally(() => {
      store.commit("SET_LOADING", false);
    });
}

function changeAvatar(avatar: string) {
  if (props.collection.avatar) {
    updateAvatar(avatar);
  } else {
    addAvatar(avatar);
  }
}

function delAvatar() {
  showAvatarDel.value = true;
}

function delAvatarConfirm() {
  if (!props.collection?.avatar) return;

  store.commit("SET_LOADING", true);

  avatarDel(props.collection.collection_id)
    .then((response) => {
      const { success, message } = response;

      toast({ message: message, type: success ? "success" : "error" });

      emit("update:data");
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    })
    .finally(() => {
      showAvatarDel.value = false;
      store.commit("SET_LOADING", false);
    });
}
</script>

<template>
  <div class="flex justify-center">
    <base-avatar
      position="center"
      size="20rem"
      :is-edit="true"
      :show-image="true"
      :url="collection?.avatar?.large"
      :aspect-ratio="1"
      border-radius="0.25rem"
      @change="changeAvatar($event)"
      @del="delAvatar()"
    />
  </div>

  <form-collection
    :data="collection"
    :type="type"
    :is-patch="true"
    @update:data="patchData($event)"
  />

  <list-translate
    :owner-id="collectionId"
    :language="locale.toLowerCase()"
    :fields="translationFields"
  />

  <base-confirm
    :show="showAvatarDel"
    :title="t('common.modals.titles.remove_image').toUpperCase()"
    :message="t('common.modals.messages.remove_image')"
    @cancel="showAvatarDel = false"
    @confirm="delAvatarConfirm()"
  />
</template>
